import React from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { styled } from '@mui/material/styles';

export type Sport = 'football' | 'basketball';

interface SportSelectorProps {
  sport: Sport;
  onChange: (sport: Sport) => void;
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  showActiveLabel?: boolean;
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButtonGroup-grouped': {
    margin: theme.spacing(0.5),
    border: 0,
    '&.Mui-disabled': {
      border: 0,
    },
    '&:not(:first-of-type)': {
      borderRadius: theme.shape.borderRadius,
    },
    '&:first-of-type': {
      borderRadius: theme.shape.borderRadius,
    },
  },
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  '&.MuiToggleButton-root': {
    padding: '8px 16px',
    fontSize: '1rem',
    fontWeight: 500,
    textTransform: 'none',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

export const SportSelector: React.FC<SportSelectorProps> = ({
  sport,
  onChange,
  size = 'medium',
  fullWidth = false,
  showActiveLabel = false,
}) => {
  const handleChange = (_event: React.MouseEvent<HTMLElement>, newSport: Sport | null) => {
    if (newSport !== null) {
      onChange(newSport);
    }
  };

  return (
    <StyledToggleButtonGroup
      value={sport}
      exclusive
      onChange={handleChange}
      aria-label="sport selection"
      size={size}
      fullWidth={fullWidth}
    >
      <StyledToggleButton value="football" aria-label="football">
        🏈 Football{showActiveLabel && sport === 'football' && ' • Active'}
      </StyledToggleButton>
      <StyledToggleButton value="basketball" aria-label="basketball">
        🏀 Basketball{showActiveLabel && sport === 'basketball' && ' • Active'}
      </StyledToggleButton>
    </StyledToggleButtonGroup>
  );
};

export default SportSelector;
